import * as Sentry from "@sentry/react";
import { redactSensitiveFields, redactForApiTelemetry } from "@/lib/pii";

/**
 * Configuración de Sentry para el frontend
 *
 * CARACTERÍSTICAS:
 * - Captura automática de errores no manejados
 * - Tracking de errores de React (Error Boundaries)
 * - Performance monitoring
 * - Breadcrumbs para contexto
 * - Solo activo en producción
 */

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// Permitir habilitar Sentry en desarrollo con variable de entorno
const forceSentry = import.meta.env.VITE_SENTRY_ENABLED === 'true';
const isProduction = import.meta.env.PROD || forceSentry;

export function initSentry() {
  // Solo inicializar si está en producción o forzado + existe el DSN
  if (!isProduction || !SENTRY_DSN) {
    const mode = import.meta.env.MODE || 'development';
    console.log(`ℹ️  Sentry: Deshabilitado (mode: ${mode})`);
    console.log('   Para habilitar en desarrollo: VITE_SENTRY_ENABLED=true en .env');
    return;
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: import.meta.env.MODE || 'production',

      // Release tracking
      release: import.meta.env.VITE_APP_VERSION || 'unknown',

      // Habilitar captura de logs de consola
      enableLogs: true,

      // Integración con React
      integrations: [
        // Captura errores de React components
        Sentry.browserTracingIntegration(),

        // Captura de logs de consola (log, warn, error)
        Sentry.consoleIntegration({
          levels: ['log', 'warn', 'error'],
        }),

        // Replay de sesiones (opcional, comentado por ahora)
        // Sentry.replayIntegration({
        //   maskAllText: true,
        //   blockAllMedia: true,
        // }),
      ],

      // Performance Monitoring
      tracesSampleRate: 0.1, // 10% de las transacciones

      // Session Replay (si lo habilitas)
      // replaysSessionSampleRate: 0.1,
      // replaysOnErrorSampleRate: 1.0,

      // Capturar errores no manejados
      beforeSend(event, hint) {
        const error = hint.originalException;

        // Agregar info del navegador
        if (typeof window !== 'undefined') {
          event.contexts = event.contexts || {};
          event.contexts.browser_info = {
            userAgent: navigator.userAgent,
            language: navigator.language,
            cookieEnabled: navigator.cookieEnabled,
          };
        }

        // No enviar errores de extensiones del navegador
        if (
          error &&
          typeof error === 'object' &&
          'message' in error &&
          typeof error.message === 'string' &&
          (error.message.includes('chrome-extension://') ||
            error.message.includes('moz-extension://'))
        ) {
          return null;
        }

        return event;
      },

      // Ignorar errores específicos del navegador
      ignoreErrors: [
        // Errores de red del navegador
        'Network request failed',
        'NetworkError',
        'Failed to fetch',

        // Errores de ResizeObserver (comunes y no críticos)
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',

        // Errores de timeout esperados
        'timeout',

        // Errores de ad blockers
        'blocked by client',
      ],

      // Enriquecer breadcrumbs con información detallada de network requests
      beforeBreadcrumb(breadcrumb, hint) {
        // No capturar breadcrumbs de health checks
        if (breadcrumb.message && breadcrumb.message.includes('/api/health')) {
          return null;
        }

        // Enriquecer breadcrumbs HTTP con headers, payload y response
        if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
          const { xhr, response, input } = hint || {};

          // Agregar información del request
          if (xhr) {
            breadcrumb.data = breadcrumb.data || {};

            // Headers del request (si están disponibles)
            try {
              const requestHeaders: Record<string, string> = {};
              if (xhr.getAllResponseHeaders) {
                // Los headers de request no son accesibles en XHR directamente
                // pero podemos agregar los de response
                const responseHeaders = xhr.getAllResponseHeaders();
                if (responseHeaders) {
                  breadcrumb.data.response_headers = responseHeaders;
                }
              }

              // Status code
              if (xhr.status) {
                breadcrumb.data.status_code = xhr.status;
                breadcrumb.data.status_text = xhr.statusText;
              }

              // Response body (limitado a primeros 1000 caracteres)
              if (xhr.responseText) {
                try {
                  const responseData = JSON.parse(xhr.responseText);
                  breadcrumb.data.response = responseData;
                } catch {
                  // Si no es JSON, guardar como texto (truncado)
                  breadcrumb.data.response = xhr.responseText.substring(0, 1000);
                }
                // El cuerpo puede llevar PII (leads): se redacta antes de salir.
                breadcrumb.data = redactSensitiveFields(breadcrumb.data);
              }
            } catch (error) {
              console.error('Error enriching XHR breadcrumb:', error);
            }
          }

          // Para fetch API
          if (input) {
            breadcrumb.data = breadcrumb.data || {};

            // URL y método
            if (typeof input === 'string') {
              breadcrumb.data.url = input;
            } else if (input instanceof Request) {
              breadcrumb.data.url = input.url;
              breadcrumb.data.method = input.method;

              // Headers del request
              try {
                const headers: Record<string, string> = {};
                input.headers.forEach((value, key) => {
                  // No capturar headers sensibles
                  if (!['authorization', 'cookie', 'x-api-key'].includes(key.toLowerCase())) {
                    headers[key] = value;
                  }
                });
                breadcrumb.data.request_headers = headers;
              } catch (error) {
                console.error('Error getting fetch headers:', error);
              }
            }

            // Response
            if (response) {
              breadcrumb.data.status_code = response.status;
              breadcrumb.data.status_text = response.statusText;
            }
          }
        }

        return breadcrumb;
      },
    });

    // Contexto global de la aplicación
    Sentry.setContext('application', {
      name: 'vivi-con-amor-y-arte-frontend',
      version: import.meta.env.VITE_APP_VERSION || 'unknown',
    });

    console.log('✅ Sentry: Inicializado correctamente');
  } catch (error) {
    console.error('❌ Error inicializando Sentry:', error);
  }
}

/**
 * Wrapper para console.error que también envía a Sentry en producción
 * Reemplaza los console.error en el código con este logger
 */
export const logger = {
  /**
   * Log de error que también se envía a Sentry
   */
  error: (message: string, error?: unknown, context?: Record<string, any>) => {
    // Siempre loguear en consola
    console.error(message, error, context);

    // En producción, enviar a Sentry
    if (isProduction && SENTRY_DSN) {
      try {
        Sentry.withScope((scope) => {
          // Agregar contexto adicional
          if (context) {
            scope.setContext('additional_info', context);

            // Agregar tags si están en el contexto
            if (context.tags) {
              Object.entries(context.tags).forEach(([key, value]) => {
                scope.setTag(key, String(value));
              });
            }
          }

          // Capturar como error si es un Error object, sino como mensaje
          if (error instanceof Error) {
            Sentry.captureException(error);
          } else {
            Sentry.captureMessage(message, 'error');
          }
        });
      } catch (sentryError) {
        console.error('Failed to send to Sentry:', sentryError);
      }
    }
  },

  /**
   * Log de warning que se envía a Sentry en producción
   */
  warn: (message: string, context?: Record<string, any>) => {
    console.warn(message, context);

    if (isProduction && SENTRY_DSN) {
      try {
        Sentry.withScope((scope) => {
          if (context) {
            scope.setContext('warning_context', context);
          }
          Sentry.captureMessage(message, 'warning');
        });
      } catch (sentryError) {
        console.error('Failed to send warning to Sentry:', sentryError);
      }
    }
  },

  /**
   * Log de info (solo consola, no Sentry)
   */
  info: (message: string, ...args: any[]) => {
    console.log(message, ...args);
  },

  /**
   * Log de debug (solo consola en desarrollo)
   */
  debug: (message: string, ...args: any[]) => {
    if (!isProduction) {
      console.log(message, ...args);
    }
  },
};

/**
 * Capturar excepciones manualmente con contexto enriquecido
 */
export function captureException(error: Error, context?: {
  userId?: string;
  operation?: string;
  extra?: Record<string, any>;
  tags?: Record<string, string>;
}) {
  // Siempre loguear en consola
  console.error('Exception captured:', error, context);

  if (!isProduction || !SENTRY_DSN) {
    return;
  }

  try {
    Sentry.withScope((scope) => {
      // Agregar usuario si existe
      if (context?.userId) {
        scope.setUser({ id: context.userId });
      }

      // Agregar tags
      if (context?.tags) {
        Object.entries(context.tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      // Agregar operación
      if (context?.operation) {
        scope.setTag('operation', context.operation);
      }

      // Agregar contexto extra
      if (context?.extra) {
        scope.setContext('additional_context', context.extra);
      }

      Sentry.captureException(error);
    });
  } catch (sentryError) {
    console.error('Failed to capture exception in Sentry:', sentryError);
  }
}

/**
 * Agregar breadcrumb manual para tracking de flujo
 *
 * IMPORTANTE: Los breadcrumbs solo se GUARDAN cuando ocurre un error.
 * Si no hay errores, Sentry no los almacena (para no saturar el dashboard).
 */
export function addBreadcrumb(
  message: string,
  category: string = 'manual',
  data?: Record<string, any>,
  level: 'info' | 'warning' | 'error' = 'info'
) {
  // Verificar que Sentry esté habilitado
  // Cambiado: ahora funciona tanto en dev (si VITE_SENTRY_ENABLED=true) como en prod
  if (!SENTRY_DSN) {
    return;
  }

  // Solo agregar breadcrumbs si Sentry está realmente inicializado
  const sentryEnabled = isProduction || import.meta.env.VITE_SENTRY_ENABLED === 'true';
  if (!sentryEnabled) {
    return;
  }

  try {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      data: {
        ...data,
        timestamp: new Date().toISOString(),
      },
    });
  } catch (error) {
    console.error('Failed to add breadcrumb:', error);
  }
}

/**
 * Configurar contexto de usuario en Sentry
 */
export function setUserContext(user: {
  id: string;
  email?: string;
  username?: string;
}) {
  if (!isProduction || !SENTRY_DSN) {
    return;
  }

  try {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });
  } catch (error) {
    console.error('Failed to set user context:', error);
  }
}

/**
 * Limpiar contexto de usuario (ej: después de logout)
 */
export function clearUserContext() {
  if (!isProduction || !SENTRY_DSN) {
    return;
  }

  try {
    Sentry.setUser(null);
  } catch (error) {
    console.error('Failed to clear user context:', error);
  }
}

/**
 * Helper especializado para capturar errores de API con contexto completo
 * Incluye request, response, headers y payload
 */
export function captureAPIError(error: any, additionalContext?: Record<string, any>) {
  if (!isProduction || !SENTRY_DSN) {
    return;
  }

  try {
    const errorInfo: Record<string, any> = {
      message: error.message || 'Unknown API Error',
      type: 'api_error',
    };

    // Información del request
    if (error.config) {
      errorInfo.request = {
        method: error.config.method?.toUpperCase(),
        url: error.config.url,
        baseURL: error.config.baseURL,
        // Headers sin información sensible
        headers: error.config.headers ?
          Object.fromEntries(
            Object.entries(error.config.headers).filter(
              ([key]) => !['authorization', 'cookie', 'x-api-key'].includes(key.toLowerCase())
            )
          ) : undefined,
        // Payload del request (PII redactada en rutas sensibles)
        data: error.config.data
          ? JSON.stringify(
              redactForApiTelemetry(error.config.url, error.config.data)
            ).substring(0, 2000)
          : undefined,
        params: error.config.params,
      };
    }

    // Información de la respuesta
    if (error.response) {
      errorInfo.response = {
        status: error.response.status,
        statusText: error.response.statusText,
        // Headers de respuesta
        headers: error.response.headers ?
          Object.fromEntries(
            Object.entries(error.response.headers).slice(0, 20)
          ) : undefined,
        // Data de respuesta (error del servidor) — PII redactada en rutas sensibles
        data: error.response.data
          ? JSON.stringify(
              redactForApiTelemetry(error.config?.url, error.response.data)
            ).substring(0, 2000)
          : undefined,
      };
    }

    // Si es un error de red (no llegó al backend)
    if (!error.response && error.request) {
      errorInfo.network_error = {
        message: 'No se pudo conectar con el servidor',
        request_sent: true,
        response_received: false,
        possible_causes: [
          'Servidor offline',
          'CORS bloqueado',
          'Red no disponible',
          'Timeout',
          'Firewall/bloqueador'
        ]
      };
    }

    // Capturar con Sentry
    Sentry.withScope((scope) => {
      // Tags para filtrado
      scope.setTag('error_type', 'api_error');
      scope.setTag('http_status', error.response?.status || 'network_error');
      scope.setTag('http_method', error.config?.method?.toUpperCase() || 'unknown');

      // Contexto completo
      scope.setContext('api_error', errorInfo);

      // Contexto adicional del llamador
      if (additionalContext) {
        scope.setContext('additional_context', additionalContext);
      }

      // Capturar el error
      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(
          `API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status || 'Network Error'}`,
          'error'
        );
      }
    });
  } catch (sentryError) {
    console.error('Failed to capture API error in Sentry:', sentryError);
  }
}

export { Sentry };
